import React, { useState, useEffect } from 'react';
import { Container, Grid, Button, Typography, Card, CircularProgress } from '@mui/material';
import Page from '../../../components/Page';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { Select, TextField } from 'src/components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import '../../styles/addstudent.css';
import { Studentimg } from 'src/images';
import palette from 'src/theme/palette';
import BasicTabs from 'src/components/Tabs';
import { viewStudentTabs, settingsTabs } from 'src/utils/data';
import { ToastNotifications } from 'src/components';

import { authService } from 'src/services/auth/auth.service';
import { useAuth } from 'src/context/auth/AuthContext';
import { studentService } from 'src/services/student.service';

const editProfileValidationSchema = Yup.object({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email format').required('Email is required'),
});

export default function EditProfile() {
  const navigate = useNavigate();
  const [fileError, setFileError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [profilePic, setProfilePic] = useState<File | null>(null);
  const { user: currentUser, getBaseRoute } = useAuth();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await authService.getCurrentUser(currentUser.id);
        console.log('userData:', userData);
        console.log('currentUser:', currentUser);
        formik.setValues({
          firstName: userData.firstname || '',
          lastName: userData.lastname || '',
          email: userData.email || '',
          studentId: userData.id || '',
          photourl: userData.photourl || '',
        });
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, []);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      studentId: '',
      photourl: '',
    },
    validationSchema: editProfileValidationSchema,
    onSubmit: async (values) => {
      try {
        await authService.editProfile(currentUser.id, values);
        ToastNotifications.success('Profile updated successfully');
        // formik.resetForm();
      } catch (error) {
        console.error('Error updating profile:', error);
        ToastNotifications.error(error.message || 'Failed to update profile. Please try again later.');
      }
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileType = file.type;
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(fileType)) {
        setFileError('Only .JPG, .JPEG, or .PNG files are allowed.');
      } else {
        setFileError('');
      }
    }
  };

  const handleProfilePicChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      handleProfilePicUpload(event.target.files[0]);
    }
  };

  const handleProfilePicUpload = async (file: File) => {
    if (!file || !currentUser.id) return;

    try {
      setLoading(true);
      const publicUrl = await studentService.updateProfilePicture(currentUser.id, file);

      formik.setFieldValue('photourl', publicUrl);
    } catch (error) {
      ToastNotifications.error('Failed to update profile picture');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProfilePic = async () => {
    if (!currentUser.id || !formik.values.photourl) return;

    try {
      await studentService.deleteProfilePicture(currentUser.id);

      formik.setFieldValue('photourl', null);
    } catch (error) {
      ToastNotifications.error('Failed to delete profile picture');
    } finally {
      setLoading(false);
    }
  };

  console.log('formik.errors:', formik.errors);

  return (
    <Page title="Add New Student">
      <Container maxWidth={false}>
        <BasicTabs
          value={activeTab}
          handleChange={(event, newValue) => {
            if (newValue === 1) {
              navigate(getBaseRoute('/change-password'));
              // setActiveTab(newValue);
            }
          }}
          tabs={settingsTabs}
        />
        <Card style={{ borderRadius: 16, marginTop: 30, padding: 20 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={2} md={1} lg={1}>
              {formik.values.photourl ? (
                <img
                  src={formik.values.photourl}
                  style={{ height: 100, width: 100, borderRadius: 100, border: '1px solid #ccc' }}
                />
              ) : (
                <div
                  style={{
                    width: 100,
                    height: 100,
                    borderRadius: 100,
                    marginRight: 20,
                    marginTop: 10,
                    border: '1px solid #ccc',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 40,
                    color: '#ccc',
                    backgroundColor: '#f0f0f0',
                  }}
                >
                  {formik.values.firstName.charAt(0)}
                </div>
              )}
            </Grid>
            <Grid item xs={12} sm={10} md={10} lg={10}>
              <Typography variant="subtitle1" color={'var(--grey-600)'}>
                only support .JPG, .JPEG, or .PNG file.
              </Typography>

              <div className="upload_row">
                <input type="file" id="_upload_pic" onChange={handleProfilePicChange} />
                <label className="_btn" htmlFor="_upload_pic">
                  Upload New photo
                </label>
                <button
                  className="_btn"
                  style={{ backgroundColor: 'var(--white)', color: 'var(--primary-main)' }}
                  onClick={handleDeleteProfilePic}
                >
                  Delete Photo
                </button>
              </div>
              {fileError && (
                <Typography style={{ fontSize: 10, fontWeight: 700, marginTop: 10 }} color="error">
                  {fileError}
                </Typography>
              )}
            </Grid>
          </Grid>
          <br />
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  placeholder="Enter First Name"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  name="firstName"
                  error={Boolean(formik.touched.firstName && formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  placeholder="Enter Last Name"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  name="lastName"
                  error={Boolean(formik.touched.lastName && formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  disabled
                  placeholder="Enter Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  name="email"
                  error={Boolean(formik.touched.email && formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              {getBaseRoute() === '/student' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Student ID"
                    placeholder="Enter Student ID"
                    value={formik.values.studentId}
                    onChange={formik.handleChange}
                    name="studentId"
                    error={Boolean(formik.touched.studentId && formik.errors.studentId)}
                    helperText={formik.touched.studentId && formik.errors.studentId}
                  />
                </Grid>
              )}
            </Grid>
            <div className="_add_st_footer_row" style={{ marginTop: 15 }}>
              <Button type="submit" variant="contained" color="primary" style={{ height: 48, width: 120, margin: 5 }}>
                {loading ? <CircularProgress color="inherit" size={20} /> : 'Save'}
              </Button>
            </div>
          </form>
        </Card>
      </Container>
    </Page>
  );
}
